import React from 'react';
import { Shield, Headphones, Database } from 'lucide-react';

interface EnterpriseCardProps {
  calendlyUrl?: string;
  isCurrentPlan?: boolean;
}

export const EnterpriseCard: React.FC<EnterpriseCardProps> = ({
  calendlyUrl = 'https://calendly.com',
  isCurrentPlan = false
}) => {
  return (
    <div className={`relative backdrop-blur-sm rounded-xl p-8 transition-all duration-300 ${
      isCurrentPlan 
        ? 'bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 border-2 border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.15)]' 
        : 'bg-zinc-900/60 border border-zinc-800 hover:scale-[1.01] hover:border-cyan-500/40 hover:shadow-xl hover:shadow-cyan-500/10'
    }`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-3xl font-bold text-zinc-50">Enterprise</h3>
            {isCurrentPlan && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                Active
              </span>
            )}
          </div>
          <p className="text-zinc-100">
            <span className="text-white font-medium">Everything in Elite</span>, plus custom solutions for organizations with advanced needs
          </p>
        </div>
        {isCurrentPlan ? (
          <div className="inline-flex items-center justify-center px-8 py-3 rounded-md text-sm font-semibold bg-gradient-to-r from-amber-500 to-yellow-500 text-black shadow-[0_0_20px_rgba(245,158,11,0.3)] whitespace-nowrap">
            Current Plan
          </div>
        ) : (
          <a
            href={calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-3 rounded-md text-sm font-semibold bg-gradient-to-r from-cyan-400 to-cyan-500 text-zinc-950 hover:from-cyan-300 hover:to-cyan-400 hover:shadow-lg transition-all duration-200 whitespace-nowrap"
          >
            Get in touch →
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* Data & Analytics */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Database size={20} className="text-cyan-400/70" />
            <h4 className="text-sm font-semibold text-zinc-100 uppercase tracking-wide">Data & Scale</h4>
          </div>
          <ul className="space-y-2 text-sm text-zinc-100">
            <li>Unlimited AI messages</li>
            <li>Unlimited queries</li>
            <li>Custom rate limits</li>
          </ul>
        </div>

        {/* Custom Data */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Database size={20} className="text-cyan-400/70" />
            <h4 className="text-sm font-semibold text-zinc-100 uppercase tracking-wide">Custom Data</h4>
          </div>
          <ul className="space-y-2 text-sm text-zinc-100">
            <li>Custom integrations</li>
            <li>Private data sources</li>
            <li>Data extractions</li>
          </ul>
        </div>

        {/* Security & Compliance */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Shield size={20} className="text-cyan-400/70" />
            <h4 className="text-sm font-semibold text-zinc-100 uppercase tracking-wide">Security</h4>
          </div>
          <ul className="space-y-2 text-sm text-zinc-100">
            <li>SSO, SAML, RBAC</li>
            <li>SOC 2, GDPR ready</li>
            <li>Private cloud deployment</li>
          </ul>
        </div>

        {/* Premium Support */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Headphones size={20} className="text-cyan-400/70" />
            <h4 className="text-sm font-semibold text-zinc-100 uppercase tracking-wide">Support</h4>
          </div>
          <ul className="space-y-2 text-sm text-zinc-100">
            <li>24/7 priority support</li>
            <li>Dedicated account manager</li>
            <li>SLA guarantees</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
